const STORAGE_KEY = "sso_post_auth_return";

export const PROFILE_MANAGEMENT_PATHS = ["/edit-profile", "/delete-profile"];

export function isProfileManagementPath(pathname) {
  return PROFILE_MANAGEMENT_PATHS.includes(pathname);
}

export function isConsentPath(pathname) {
  return pathname === "/consent";
}

function toLocationLike(loc) {
  if (!loc?.pathname) return null;
  return {
    pathname: loc.pathname,
    search: loc.search || "",
    hash: loc.hash || "",
  };
}

/** Remember profile route across OAuth round-trips (sessionStorage). */
export function persistPostAuthReturn(location) {
  const loc = toLocationLike(location);
  if (!loc || !isProfileManagementPath(loc.pathname)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
  } catch {
    // ignore quota / private mode
  }
}

export function getPersistedPostAuthReturn() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.pathname || !isProfileManagementPath(parsed.pathname)) {
      return null;
    }
    return toLocationLike(parsed);
  } catch {
    return null;
  }
}

export function clearPersistedPostAuthReturn() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Where to send the user after login/register.
 * Profile routes (from PrivateRoute or sessionStorage) win over generic home.
 */
export function resolvePostAuthDestination({ redirectUri, from }) {
  const fromLoc = toLocationLike(from);
  if (fromLoc && isProfileManagementPath(fromLoc.pathname)) {
    return fromLoc;
  }

  const persisted = getPersistedPostAuthReturn();
  if (persisted) {
    return persisted;
  }

  return toLocationLike(redirectUri);
}

export function shouldShowConsentAfterAuth(redirectUri) {
  const pathname = redirectUri?.pathname;
  return Boolean(pathname && isConsentPath(pathname));
}

export function navigatePostAuth(navigate, { redirectUri, from, location }) {
  const dest = resolvePostAuthDestination({
    redirectUri,
    from: from ?? location?.state?.from,
  });

  if (dest) {
    if (isProfileManagementPath(dest.pathname)) {
      clearPersistedPostAuthReturn();
    }
    navigate(dest, { replace: true });
    return;
  }

  navigate("/", { replace: true });
}
